// Shared referral configuration. Lives in lib so both the Mine and Referral
// API hooks share a single source — duplicating the URL across api files
// caused a one-changes-the-other-doesn't drift risk.

import { CLIENT } from '@/clients';

/** Fallback share URL used when the backend hasn't returned a real link
 *  (e.g. the user hasn't created an invitation yet). The code segment is
 *  appended at the call site. A client whose links live on another domain
 *  names it in `referral.shareBase`; otherwise the link points back at the
 *  origin this build is served from, whose `/r/:code` main.tsx handles. */
export const REFERRAL_LINK_FALLBACK_BASE =
  CLIENT.referral?.shareBase ?? `${window.location.origin}/r/`;

const TG_BASE_MAP: Record<string, string> = {
  'https://api.tt6.com': 't.me/jdskfjkd_bot/game',
  'https://api.99h.com': 't.me/GF99Hbot/game',
};

/** Base URL for Telegram invitation links, e.g. "t.me/mybot/game".
 *  When set, telegram-type links are built as `https://${TG_REFERRAL_BASE}/r/${code}`.
 *  Falls back to `window.location.origin` when empty. */
export const TG_REFERRAL_BASE =
  TG_BASE_MAP[import.meta.env.VITE_API_BASE_URL ?? ''] ?? '';

// ── Web referral-link capture ────────────────────────────────────────────────
// When a user arrives via https://app.99h.com/r/:code, main.tsx extracts the
// code before React mounts and stores it here. SignUp reads and locks the
// invitation field; after a successful registration the code is cleared.

const REFERRAL_CODE_KEY = 'hg.referral-code';

// In-memory store — survives React re-renders but resets on every page refresh
// since JS re-executes from scratch. sessionStorage is used only as a one-time
// bridge from main.tsx (which runs before React mounts) into this module.
let _referralCode: string | null = null;

export function getReferralCode(): string | null {
  if (_referralCode !== null) return _referralCode;
  // Bootstrap once from sessionStorage (written by main.tsx pre-React),
  // then immediately consume it so a refresh finds nothing.
  try {
    const stored = sessionStorage.getItem(REFERRAL_CODE_KEY);
    if (stored) {
      _referralCode = stored;
      sessionStorage.removeItem(REFERRAL_CODE_KEY);
    }
  } catch {}
  return _referralCode;
}

export function setReferralCode(code: string): void {
  _referralCode = code;
}

export function clearReferralCode(): void {
  _referralCode = null;
  try { sessionStorage.removeItem(REFERRAL_CODE_KEY); } catch {}
}

// ── Web → Telegram Mini App hand-off ─────────────────────────────────────────
// When a web visitor taps "TG Quick Sign In" we redirect them into the Mini App
// bot. If they arrived via a referral link the code is stored above, so we
// forward it as the Mini App `startapp` parameter (surfaced as `start_param` in
// initData). That lets the bot link a newly auto-created Telegram user to the
// referrer. Without a code we open the bot's default Mini App entry.

const TG_QUICK_SIGNIN_BASE = TG_REFERRAL_BASE || 't.me/GF99Hbot/game';

export function buildTgQuickSignInUrl(): string {
  const code = getReferralCode();
  return code
    ? `https://${TG_QUICK_SIGNIN_BASE}?startapp=${encodeURIComponent(code)}`
    : `https://${TG_QUICK_SIGNIN_BASE}`;
}
